import { ContentPage } from "./ContentPage";
import { PLATFORM } from "../../config/platform";

// Public, no-login page whose URL goes in the Google Play listing's
// "Delete account URL" field. Play requires it to (1) name the app or
// developer as shown on the store listing, (2) prominently show the steps to
// request deletion, and (3) state which data is deleted vs kept, plus any
// additional retention period. Keep those three things intact when editing.
export const DeleteAccountBody = () => (
  <>
    <p>
      <strong>{PLATFORM.platform_name}</strong> (developer: <strong>{PLATFORM.company}</strong>) lets you
      delete your account and associated data at any time, directly from the
      app — you don't need to contact support.
    </p>

    <h2>How to request account deletion</h2>
    <p>
      Sign in to {PLATFORM.platform_name} and follow the steps for your platform. Deletion
      takes effect immediately as a request; see "What happens next" below.
    </p>
    <ol className="list-decimal">
      <li>
        <strong>Android / iOS app:</strong> open <strong>Settings</strong> →{" "}
        <strong>Account Settings</strong> → <strong>Delete my account</strong>,
        then confirm.
      </li>
      <li>
        <strong>Web app</strong> (
        <a href={PLATFORM.web_url}>{PLATFORM.domain}</a>):
        sign in, open <strong>Profile</strong>, scroll to{" "}
        <strong>Danger zone</strong>, then select{" "}
        <strong>Delete my account</strong> and confirm.
      </li>
      <li>
        <strong>If you can't sign in:</strong> email{" "}
        <a href={`mailto:${PLATFORM.support.privacy_email}`}>
          {PLATFORM.support.privacy_email}
        </a>{" "}
        from the address on your account and we will process the request
        manually within 30 days.
      </li>
    </ol>

    <h2>What happens next</h2>
    {/* The 30-day figure is prose here because this page must work logged-out,
        so it can't read AppConfig.accountDeletionGraceDays. If a platform
        owner changes that setting, update this page, privacy.tsx and
        mobileapp's PrivacyPolicy.tsx to match. */}
    <ol className="list-decimal">
      <li>
        You are signed out of every device immediately and your account stops
        working.
      </li>
      <li>
        Your account is scheduled for deletion after a{" "}
        <strong>30-day grace period</strong>. During this window you can undo
        the request by signing back in and choosing{" "}
        <strong>Restore my account</strong>.
      </li>
      <li>
        After 30 days the account is permanently deactivated and deleted. Sign-in is
        disabled for good and the account cannot be restored.
      </li>
    </ol>

    <h2>Organizations you own</h2>
    <ul className="list-disc">
      <li>
        If you are the <strong>sole owner of an organization that still has
        other members</strong>, you must transfer ownership to another member
        before your account can be deleted. The app prompts you to choose the
        new owner as part of the deletion flow, so your team does not lose
        access to their own data.
      </li>
      <li>
        If you are the <strong>only member</strong> of your organization, that
        organization and its business records are deleted along with your
        account.
      </li>
    </ul>

    <h2>What data is deleted and what is kept</h2>

    <h3>Deleted / deactivated</h3>
    <ul className="list-disc">
      <li>
        <strong>Account profile:</strong> your name, email address, phone
        number, postal address and profile photo — deactivated at once and no
        longer used or visible anywhere in the product.
      </li>
      <li>
        <strong>Credentials and sessions:</strong> your password and all
        active sign-in sessions and tokens are revoked immediately and
        permanently.
      </li>
      <li>
        <strong>Organization memberships:</strong> your access to every
        organization is removed.
      </li>
      <li>
        <strong>Your organization,</strong> including its products,
        categories, brands, suppliers, customers, warehouses, orders, sales,
        invoices, payments, stock records and uploaded images — but only where
        you were its <em>only</em> member.
      </li>
    </ul>

    <h3>Kept, and why</h3>
    <ul className="list-disc">
      <li>
        <strong>Business records inside an organization that continues to
        exist.</strong> {PLATFORM.platform_name} is a shared business tool: products,
        stock movements, orders, sales, invoices, payments and uploaded images
        belong to the organization, not to an individual user. If the
        organization still has other members after you leave, those records
        stay with it so your colleagues' books remain complete and accurate.
      </li>
      <li>
        <strong>Records we must retain by law</strong> — for example
        transaction, invoice and tax records — are kept for the period
        required by applicable accounting and tax legislation, even after your
        account is deactivated.
      </li>
      <li>
        <strong>Limited security and audit logs</strong> (such as the date a
        deletion was requested) are kept to protect against fraud and abuse
        and to demonstrate that your request was honoured.
      </li>
      <li>
        <strong>Routine encrypted backups</strong> may still contain your data
        until they age out on their normal backup rotation. Backups are not
        used to restore deleted accounts.
      </li>
    </ul>

    <h2>Additional retention period</h2>
    <p>
      Beyond the 30-day grace period described above, deactivated account
      records and the categories listed under "Kept, and why" are retained for
      as long as needed for the purpose stated — principally to keep an
      organization's business and accounting records intact and to meet legal
      obligations. They are not used to contact you, and your account can
      never be reactivated.
    </p>
    <p>
      To ask about a specific record, or to request erasure of data that is not
      subject to a legal retention obligation, email{" "}
      <a href={`mailto:${PLATFORM.support.privacy_email}`}>
        {PLATFORM.support.privacy_email}
      </a>
      . See our <a href="/privacy-policy">Privacy Policy</a> for full details
      of how we handle personal information.
    </p>

    <h2>Contact</h2>
    <p>
      {PLATFORM.platform_name} is operated by {PLATFORM.company}. For anything related to
      deleting your account or data, contact{" "}
      <a href={`mailto:${PLATFORM.support.privacy_email}`}>
        {PLATFORM.support.privacy_email}
      </a>
      .
    </p>
  </>
);

export const DeleteAccount = ({ onBack }: { onBack: () => void }) => (
  <ContentPage title={`Delete Your ${PLATFORM.platform_name} Account`} onBack={onBack}>
    <DeleteAccountBody />
  </ContentPage>
);

export default DeleteAccount;
